import React, { useState, useEffect, useCallback } from 'react';
import { housesAPI } from '../services/api';
import HouseForm from './HouseForm';
import './HouseSelector.css';

const HouseSelector = ({ selectedHouseId, onHouseChange, isHeader = false, onToggleRef, showAsLogo = false }) => {
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadHouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleHouse = useCallback(() => {
    if (houses.length <= 1) {
      // If only one house or no houses, do nothing
      return;
    }

    // Get current house ID from localStorage or state
    const currentHouseId = selectedHouseId || parseInt(localStorage.getItem('selectedHouseId') || '0');
    
    // Find current house index
    const currentIndex = houses.findIndex(h => h.id === currentHouseId);
    // Get next house (wrap around if at end)
    const nextIndex = (currentIndex + 1) % houses.length;
    const nextHouse = houses[nextIndex];
    
    if (nextHouse && nextHouse.id !== currentHouseId) {
      localStorage.setItem('selectedHouseId', nextHouse.id.toString());
      onHouseChange(nextHouse.id);
    }
  }, [houses, onHouseChange]); // Removed selectedHouseId from dependencies to prevent infinite loop

  // Expose toggle function to parent via ref callback
  useEffect(() => {
    if (onToggleRef) {
      onToggleRef(handleToggleHouse);
    }
  }, [onToggleRef, handleToggleHouse]);

  const loadHouses = async () => {
    try {
      const response = await housesAPI.getAll();
      const housesData = Array.isArray(response.data) ? response.data : [];
      // Sort houses by ID to ensure consistent ordering
      housesData.sort((a, b) => (a.id || 0) - (b.id || 0));
      setHouses(housesData);
      // Do NOT restore or change selection here - App handles it from localStorage
      // This prevents race conditions and random house selection on refresh
    } catch (error) {
      console.error('Error loading houses:', error);
      // Don't show error if it's a network error - backend might not be running
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        console.warn('Backend server appears to be offline. Please ensure the backend is running on http://localhost:8000');
      }
    } finally {
      setLoading(false);
    }
  };


  const handleHouseCreated = (newHouse) => {
    loadHouses();
    // Select the newly created house
    localStorage.setItem('selectedHouseId', newHouse.id);
    onHouseChange(newHouse.id);
    setShowForm(false);
  };

  const selectedHouse = houses.find(h => h.id === selectedHouseId);

  if (loading) {
    return isHeader ? (
      <div className="house-selector-header-loading">Loading...</div>
    ) : (
      <div className="house-selector-loading">Loading houses...</div>
    );
  }

  if (isHeader) {
    if (showAsLogo) {
      // Show as part of logo - just the house name text
      return (
        <div className="house-selector-logo-text">
          {selectedHouse ? (
            <span className="house-name-text">
              {selectedHouse.name}
              {houses.length > 1 && (
                <svg 
                  className="house-chevron-small" 
                  viewBox="0 0 24 24" 
                  fill="none"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </span>
          ) : (
            <span className="house-name-text-placeholder">Select House</span>
          )}
        </div>
      );
    }
    
    // Regular header button version
    return (
      <div className="house-selector-header">
        <button
          type="button"
          className="house-selector-trigger"
          onClick={handleToggleHouse}
          aria-label={houses.length > 1 ? "Switch to next house" : "No other houses available"}
          title={houses.length > 1 ? `Click to switch house (${houses.length} available)` : "Only one house available"}
        >
          <span className="house-selector-text">
            {selectedHouse ? selectedHouse.name : 'Select House'}
          </span>
          {houses.length > 1 && (
            <svg 
              className="house-chevron" 
              viewBox="0 0 24 24" 
              fill="none"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )}
        </button>
      </div>
    );
  }

  // Legacy non-header version (for backward compatibility)
  const handleSelect = (houseId) => {
    if (houseId) {
      localStorage.setItem('selectedHouseId', houseId);
      onHouseChange(houseId);
    }
  };

  return (
    <div className="house-selector">
      <label htmlFor="house-select">Select House:</label>
      <select
        id="house-select"
        value={selectedHouseId || ''}
        onChange={(e) => handleSelect(parseInt(e.target.value))}
        className="house-select"
      >
        {houses.length === 0 && (
          <option value="">No houses available</option>
        )}
        {houses.map((house) => (
          <option key={house.id} value={house.id}>
            {house.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default HouseSelector;

